import { useRef, useState, useMemo } from "react";
import { FiSliders } from "react-icons/fi";
import { StatSelect } from "./components/StatSelect";
import { StatsFilterModal } from "../../../components/coach/dashboard/StatsFilterModal";
import { SimpleDropdown } from "../../../components/common/SimpleDropdown";

export const RecentTeamStats = ({
  onFiltersClick,
  showFilterModal,
  onCloseModal,
  onApplyFilters,
  stats = {},
  hideFilters = false,
  statsType = 'overall',
  onStatsTypeChange,
  category = 'hitting',
  onCategoryChange,
}) => {
  const buttonRef = useRef(null);

  // State for selected stats
  const [selectedBattingStat, setSelectedBattingStat] = useState("AVG");
  const [selectedPitchingStat, setSelectedPitchingStat] = useState("ERA");
  const [selectedFieldingStat, setSelectedFieldingStat] = useState("FLD%");

  // Dropdown options for stats type
  const statsTypeOptions = [
    { value: "overall", label: "Overall Stats" },
    { value: "conference", label: "Conference Stats" },
  ];

  // Dropdown options for category
  const categoryOptions = [
    { value: "", label: "Full Roster" },
    { value: "hitting", label: "Hitters" },
    { value: "pitching", label: "Pitchers" },
  ];

  // Batting stat options for SimpleDropdown
  const battingOptions = [
    { value: "AVG", label: "AVG" },
    { value: "OBP", label: "OBP" },
    { value: "SLG", label: "SLG" },
    { value: "ISO", label: "ISO" }
  ];

  const pitchingOptions = [
    { value: "ERA", label: "ERA" },
    { value: "K%", label: "K%" },
    { value: "BB%", label: "BB%" }
  ];

  const fieldingOptions = [
    { value: "FLD%", label: "FLD%" },
    { value: "E", label: "E" },
    { value: "DP", label: "DP" }
  ];

  // Calculate batting stat value
  const battingValue = useMemo(() => {
    switch (selectedBattingStat) {
      case "AVG":
        return stats.avg || stats.teamAverage || stats.battingAverage;
      case "OBP":
        return stats.obp || stats.onBasePercentage;
      case "SLG":
        return stats.slg || stats.sluggingPercentage;
      case "ISO":
        return stats.iso || stats.isolatedPower;
      default:
        return "N/A";
    }
  }, [selectedBattingStat, stats]);

  // Calculate pitching stat value
  const pitchingValue = useMemo(() => {
    switch (selectedPitchingStat) {
      case "ERA":
        return stats.era || stats.teamERA;
      case "K%": {
        // K% = (SO / BF) × 100
        // BF = AB + BB + HBP + SF + SH
        const so = stats.so || stats.strikeouts || 0;
        const ab = stats.ab || stats.atBats || 0;
        const bb = stats.bb || stats.walks || 0;
        const hbp = stats.hbp || stats.hitByPitch || 0;
        const sf = stats.sf || stats.sacrificeFlies || 0;
        const sh = stats.sh || stats.sacrificeHits || 0;

        const bf = ab + bb + hbp + sf + sh;
        if (bf > 0) {
          const kPercent = ((so / bf) * 100).toFixed(1);
          return `${kPercent}%`;
        }
        return;
      }
      case "BB%": {
        // BB% = (BB / BF) × 100
        // BF = AB + BB + HBP + SF + SH
        const bb = stats.bb || stats.walks || 0;
        const ab = stats.ab || stats.atBats || 0;
        const hbp = stats.hbp || stats.hitByPitch || 0;
        const sf = stats.sf || stats.sacrificeFlies || 0;
        const sh = stats.sh || stats.sacrificeHits || 0;

        const bf = ab + bb + hbp + sf + sh;
        if (bf > 0) {
          const bbPercent = ((bb / bf) * 100).toFixed(1);
          return `${bbPercent}%`;
        }
        return;
      }
      default:
        return;
    }
  }, [selectedPitchingStat, stats]);

  const fieldingValue = useMemo(() => {
    switch (selectedFieldingStat) {
      case "FLD%":
        return stats.fld || stats.fieldingPercentage || "";
      case "E":
        return stats.errors || "";
      case "DP":
        return stats.doublePlays || "";
      default:
        return "N/A";
    }
  }, [selectedFieldingStat, stats]);

  return (
    <div className="flex flex-col lg:flex-row lg:items-center justify-between pb-4 gap-3 relative">
      {/* Left: title + dropdowns */}
      <div className="flex-1 min-w-0">
        <p className="text-jp-muted text-[10px] font-bold uppercase tracking-widest mb-2 text-left">
          2025–26 Season
        </p>

        {/* Stats Type & Category Dropdowns */}
        <div className="flex flex-wrap items-center gap-3">
          {onStatsTypeChange && (
            <div style={{ minWidth: '150px' }} className="[&_button]:h-8 [&_button]:py-0 [&_button]:px-3 [&_button]:text-[11px] [&_button]:!border [&_button]:border-jp-line [&_svg]:!w-3 [&_svg]:!h-3">
              <SimpleDropdown
                value={statsType}
                onChange={onStatsTypeChange}
                options={statsTypeOptions}
                placeholder="Overall Stats"
                variant="pill"
              />
            </div>
          )}
          {onCategoryChange && (
            <div style={{ minWidth: '130px' }} className="[&_button]:h-8 [&_button]:py-0 [&_button]:px-3 [&_button]:text-[11px] [&_button]:!border [&_button]:border-jp-line [&_svg]:!w-3 [&_svg]:!h-3">
              <SimpleDropdown
                value={category}
                onChange={onCategoryChange}
                options={categoryOptions}
                placeholder="Full Roster"
                variant="pill"
              />
            </div>
          )}
        </div>

        {/* <div className="flex flex-wrap items-center gap-3">
          <StatSelect label="Streak" value={stats.streak || stats.currentStreak || "N/A"} disabled />

   
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-jp-muted">
              {selectedBattingStat}
            </span>
            <div className="flex items-center gap-2">
              <div style={{ minWidth: '70px' }} className="[&_button]:h-8 [&_button]:py-0 [&_button]:px-3 [&_button]:text-[11px] [&_button]:!border [&_button]:border-jp-line [&_svg]:!w-3 [&_svg]:!h-3">
                <SimpleDropdown
                  value={selectedBattingStat}
                  onChange={setSelectedBattingStat}
                  options={battingOptions}
                  placeholder="AVG"
                  variant="pill"
                />
              </div>
              <span className="text-[11px] text-jp-ink">{battingValue}</span>
            </div>
          </div>

          
          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-jp-muted">
              {selectedPitchingStat}
            </span>
            <div className="flex items-center gap-2">
              <div style={{ minWidth: '70px' }} className="[&_button]:h-8 [&_button]:py-0 [&_button]:px-3 [&_button]:text-[11px] [&_button]:!border [&_button]:border-jp-line [&_svg]:!w-3 [&_svg]:!h-3">
                <SimpleDropdown
                  value={selectedPitchingStat}
                  onChange={setSelectedPitchingStat}
                  options={pitchingOptions}
                  placeholder="ERA"
                  variant="pill"
                />
              </div>
              <span className="text-[11px] text-jp-ink">{pitchingValue}</span>
            </div>
          </div>

          <div className="flex flex-col gap-1">
            <span className="text-[10px] uppercase tracking-wide text-jp-muted">
              {selectedFieldingStat}
            </span>
            <div className="flex items-center gap-2">
              <div style={{ minWidth: '70px' }} className="[&_button]:h-8 [&_button]:py-0 [&_button]:px-3 [&_button]:text-[11px] [&_button]:!border [&_button]:border-jp-line [&_svg]:!w-3 [&_svg]:!h-3">
                <SimpleDropdown
                  value={selectedFieldingStat}
                  onChange={setSelectedFieldingStat}
                  options={fieldingOptions}
                  placeholder="FLD%"
                  variant="pill"
                />
              </div>
              <span className="text-[11px] text-jp-ink">{fieldingValue}</span>
            </div>
          </div>

          <div className="flex flex-col gap-1 min-w-[240px]">
            <span className="text-[10px] uppercase tracking-wide text-jp-muted">
              Top Performer
            </span>
            <span className="text-[11px] text-jp-ink truncate">
              {stats.topPerformer || stats.leadingPlayer || "No data available"}
            </span>
          </div>
        </div> */}
      </div>

      {/* Right: text + icon, no pill/button box in desktop look */}
      {!hideFilters && (
        <>
          <button
            ref={buttonRef}
            type="button"
            onClick={onFiltersClick}
            className="self-start lg:self-center inline-flex items-center gap-2 text-jp-ink text-sm whitespace-nowrap
                       px-0 py-0 hover:text-jp-accent transition-colors"
          >
            Add/Subtract Filters
            <FiSliders className="text-jp-ink" size={18} />
          </button>

          {/* Stats Filter Modal */}
          <StatsFilterModal
            isOpen={showFilterModal}
            onClose={onCloseModal}
            onApply={onApplyFilters}
            buttonRef={buttonRef}
          />
        </>
      )}
    </div>
  );
};
